import { browser } from '$app/environment';
import { createSubscriptionClient } from '$lib/ws.js';
import type { UserResponse } from '$types/races';
import { isUserSubscribed } from '$utils/auth.js';
import { createConfigStore } from '$utils/ui.js';
import { redirect } from '@sveltejs/kit';

import { get, writable, type Writable } from 'svelte/store';
import type { LayoutLoad } from './$types';
import { getUser } from '$utils/user';

function parseUserLayouts(user: UserResponse) {
  let layoutConfigStore = createConfigStore();
  // if we failed loading the user, just use default configs

  if (!browser || !user?.data?.user.layoutConfigs) return layoutConfigStore;

  if (!isUserSubscribed(user?.data?.user)) {
    layoutConfigStore = createConfigStore({ isFree: true });
  }

  // get the user layouts from their profile and append to defaults
  if (isUserSubscribed(user?.data?.user))
    user?.data?.user.layoutConfigs.forEach((layoutConfig) => {
      layoutConfigStore.addOne(layoutConfig, { mutate: false });
    });

  //   check to make sure the loaded active layout is still valid
  if (browser) {
    const activeLayoutConfigName = get(
      layoutConfigStore.activeLayoutConfigName
    );
    if (!layoutConfigStore.hasConfig(activeLayoutConfigName)) {
      layoutConfigStore.activeLayoutConfigName.set('Default Win');
    }
  }

  return layoutConfigStore;
}

export const load: LayoutLoad = async ({
  depends,
  parent,
  fetch,
  route,
  setHeaders
}) => {
  depends('betwatch:user');
  depends('supabase:auth');

  const { session, guestId } = await parent();

  const user = session
    ? await getUser(fetch, { accessToken: session?.access_token, guestId })
    : undefined;

  if (session && !user?.data?.user.name) {
    if (!route.id?.startsWith('/app/profile')) {
      redirect(303, '/app/profile');
    }
  }

  const layoutConfigStore = user
    ? parseUserLayouts(user)
    : createConfigStore({ isFree: true });

  const subscriptionClient =
    browser && session?.access_token
      ? createSubscriptionClient(session.access_token)
      : undefined;

  // feature flags
  const featureFlags = writable([]) as Writable<string[]>;

  setHeaders({
    'Cache-Control': 'no-store, no-cache, must-revalidate, proxy-revalidate',
    Pragma: 'no-cache',
    Expires: '0'
  });

  return { user, layoutConfigStore, subscriptionClient, featureFlags };
};
